import type { Patch } from "@freightsimple/shared/types";
import BookShipmentNavigationFooter2 from "./CustomComponents/BookShipmentNavigationFooter2";
import BookShipmentScreenLayout from "./CustomComponents/BookShipmentScreenLayout";
import type { AddBrokerBrokerType } from "@/Shared/Types/brokerTypes";
import { emptyBrokerInfo } from "@/Shared/Types/brokerTypes";
import { brokersErrorMessagesForBroker } from "@/Shared/Brokers/Validators/brokersErrorMessagesForBroker";
import ContactInput from "@/Shared/Contacts/Inputs/ContactsInput";
import { brokersFieldLimits } from "@/Shared/Brokers/Helpers/brokersFieldLimits";
import type { AddContactContactType } from "@/Shared/Types/contactTypes";
import AddressInput from "@/Shared/Locations/Inputs/LocationsAddressInput";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { BrokerDraft } from "@freightsimple/generated-dashboard-openapi-client/models/BrokerDraft";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SetBrokerDetailsOperationRequest } from "@freightsimple/generated-dashboard-openapi-client/apis/ShipmentsApi";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { useState } from "react";
import { useDispatch } from "react-redux";
import BookShipmentSmallSection from "./CustomComponents/BookShipmentSmallSection";
import { BookShipmentBrokerScreenAlreadySaved } from "./CustomComponents/BookShipmentBrokerScreenAlreadySaved";
import { setCurrentScreenIsDirty } from "@/Slices/bookShipmentSlice";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";

// Replica of the legacy Cargo Box (Medium) frame the form panels sit in.
interface BookShipmentBrokerScreenProps {
  shipment: PreBookingShipment;
  defaultValues: BrokerDraft | undefined;
  onComplete: () => void;
  nextIdentifier: string;
  onPrevious: () => Promise<void>;
}

function BookShipmentBrokerScreen(props: BookShipmentBrokerScreenProps) {
  const { shipment, defaultValues } = props;
  const { shipmentId } = shipment;
  const [forceValidation, setForceValidation] = useState(false);
  const dispatch = useDispatch();

  const [broker, setBroker] = useState<AddBrokerBrokerType>({
    ...emptyBrokerInfo(),
    ...defaultValues,
  });

  const comparisonContacts: Map<string, AddContactContactType> = new Map();
  comparisonContacts.set("pickup", shipment.pickupContact || {});
  comparisonContacts.set("delivery", shipment.deliveryContact || {});

  const brokerErrors = brokersErrorMessagesForBroker(
    broker,
    comparisonContacts,
    shipment.pickupLocation,
    shipment.deliveryLocation,
  );
  const shipmentsApi = useShipmentsApi();

  if (shipment.broker !== undefined) {
    return (
      <div id="broker-info">
        {/* Internals only: layout mirrors the legacy screen; `underlined` is
            the Cargo-derived look the fields have always had here. */}
        <ControlPresentationProvider presentation="underlined">
          <BookShipmentScreenLayout
            pageHeading={`Customs Broker Info`}
            pageSubheading={
              <Stack gap={1}>
                <div>
                  Brokerage issues can cause delays in shipments. If there is a
                  mistake in the details you entered, you can modify them.
                </div>
                <div>
                  Learn more about brokerage{" "}
                  <Link
                    external
                    href="https://help.freightsimple.com/using-freightsimple/booking-your-shipment/customs-broker-details"
                  >
                    at FreightSimple&apos;s help center
                  </Link>
                </div>
              </Stack>
            }
          >
            <Stack gap={8}>
              <BookShipmentBrokerScreenAlreadySaved
                broker={shipment.broker}
                shipmentId={shipment.shipmentId}
              />
              <BookShipmentNavigationFooter2
                next="Next"
                onPrevious={props.onPrevious}
                nextUrl={`/book/${props.nextIdentifier}?shipmentId=${shipmentId}`}
                nextIsValid={() => {
                  props.onComplete();
                  return true;
                }}
                nextIsEnabled={() => true}
                nextIdentifier={props.nextIdentifier}
                hidePreviousButton={false}
              />
            </Stack>
          </BookShipmentScreenLayout>
        </ControlPresentationProvider>
      </div>
    );
  }

  function setAccountNumber(accountNumber: string) {
    dispatch(setCurrentScreenIsDirty());
    setBroker({
      ...broker,
      accountNumber,
    });
  }

  function isValid() {
    const anyErrorMessages = [
      brokerErrors.businessName,
      brokerErrors.accountNumber,
      brokerErrors.address.addressLine,
      brokerErrors.address.city,
      brokerErrors.address.postalCode,
      brokerErrors.address.stateOrProvinceCode,
      brokerErrors.contact.emailAddress,
      brokerErrors.contact.contactName,
      brokerErrors.contact.phoneNumber,
    ].some((e) => e !== undefined);

    return !anyErrorMessages;
  }

  async function nextIsValid() {
    setForceValidation(true);
    if (isValid()) {
      const request: SetBrokerDetailsOperationRequest = {
        setBrokerDetailsRequest: {
          shipmentId,
          broker: broker as Broker,
        },
      };

      await shipmentsApi.setBrokerDetails(request);
      props.onComplete();
      return true;
    } else {
      return false;
    }
  }

  function updateContact(contact: Partial<Contact>) {
    dispatch(setCurrentScreenIsDirty());
    setBroker({
      ...broker,
      contact: {
        ...broker.contact,
        ...contact,
      },
    });
  }

  function updateAddress(address: Patch<Address>) {
    dispatch(setCurrentScreenIsDirty());
    setBroker({
      ...broker,
      address: {
        ...broker.address,
        ...address,
      },
    });
  }

  function setBusinessName(businessName: string) {
    dispatch(setCurrentScreenIsDirty());
    setBroker({
      ...broker,
      businessName,
    });
  }

  let i = 1;

  return (
    <div id="broker-info">
      {/* Internals only: layout mirrors the legacy screen; `underlined` is
          the Cargo-derived look the fields have always had here. */}
      <ControlPresentationProvider presentation="underlined">
        <BookShipmentScreenLayout
          pageHeading={`Customs Broker Info`}
          pageSubheading={
            <Stack gap={3}>
              <div>
                Please enter the details of your customs broker. Brokerage
                issues can cause delays in shipments. Learn more about brokerage{" "}
                <Link
                  external
                  href="https://help.freightsimple.com/using-freightsimple/booking-your-shipment/customs-broker-details"
                >
                  here
                </Link>
              </div>
              <Banner
                variant="warning"
                title="FreightSimple is not a Customs Broker"
              >
                Customs clearance is your responsibility as the booker. We
                handle document relay and status tracking only.
              </Banner>
              <Banner variant="warning" title="Self-Clearing not allowed">
                You must use a customer broker to clear your freight. Attempts
                to self-clear will likely incur additional fees that you will be
                liable for.
              </Banner>
            </Stack>
          }
        >
          <BookShipmentSmallSection
            header={undefined}
            description={undefined}
            index={i++}
          >
            <Panel collapseOnNarrow>
              <AddressInput
                enabled={true}
                businessName={broker.businessName || ""}
                businessNameChanged={setBusinessName}
                distributionWarehouseBrand={undefined}
                locationType={LocationType.Warehouse}
                address={broker.address}
                addressChanged={updateAddress}
                forceValidation={forceValidation}
                businessNameErrorMessages={brokerErrors.businessName}
                addressErrorMessages={brokerErrors.address}
                addressOptions={[]}
                displayName={"Customs Broker"}
                allowCityAndPostalCodeEditing={true}
                warnAboutPotentialBusinessNameErrors={false}
                accountNumber={broker.accountNumber || ""}
                accountNumberChanged={setAccountNumber}
                accountNumberErrorMessages={brokerErrors.accountNumber}
                showAccountNumberInput={true}
                customBusinessNameLabel="Customs Broker Business Name"
                maxLengths={brokersFieldLimits}
              />
            </Panel>
          </BookShipmentSmallSection>

          <BookShipmentSmallSection
            header="Contact Details"
            description="Enter the customs broker's contact details"
            index={i++}
          >
            <Panel collapseOnNarrow>
              <ContactInput
                contact={{
                  contactName: broker.contact.contactName ?? "",
                  phoneNumber: broker.contact.phoneNumber ?? "",
                  emailAddress: broker.contact.emailAddress ?? "",
                  phoneNumberExtension:
                    broker.contact.phoneNumberExtension ?? "",
                }}
                contactChanged={updateContact}
                forceValidation={forceValidation}
                errorMessages={brokerErrors.contact}
                warningMessages={brokerErrors.contact}
                customLabels={{
                  contactName: "Customs Broker Contact Name",
                  phoneNumber: "Customs Broker Phone Number",
                  emailAddress: "Customs Broker Email Address",
                }}
                maxLengths={brokersFieldLimits}
              />
            </Panel>
          </BookShipmentSmallSection>
          <BookShipmentNavigationFooter2
            next="Next"
            onPrevious={props.onPrevious}
            nextUrl={`/book/${props.nextIdentifier}?shipmentId=${shipmentId}`}
            nextIsValid={nextIsValid}
            nextIsEnabled={() => true}
            nextIdentifier={props.nextIdentifier}
            hidePreviousButton={false}
          />
        </BookShipmentScreenLayout>
      </ControlPresentationProvider>
    </div>
  );
}

export default BookShipmentBrokerScreen;
